import { Wrench, Clock, ExternalLink, Shield, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { sanitizeUrl } from '../../../lib/sanitizeUrl';

interface ToolItem {
  id: string;
  tool_name: string;
  category: string;
  source: string;
  confidence: string;
  website_url: string;
  description: string;
}

interface Props {
  tools: ToolItem[];
  hasEnriched: boolean;
}

const confidenceConfig: Record<string, { icon: typeof Shield; label: string; color: string }> = {
  high: { icon: ShieldCheck, label: 'High', color: 'text-app-positive bg-app-positive/10' },
  medium: { icon: Shield, label: 'Medium', color: 'text-app-warning bg-app-warning/10' },
  low: { icon: ShieldQuestion, label: 'Low', color: 'text-app-muted bg-app-canvas' },
};

function groupByCategory(tools: ToolItem[]): Record<string, ToolItem[]> {
  const groups: Record<string, ToolItem[]> = {};
  for (const tool of tools) {
    const cat = tool.category || 'Other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(tool);
  }
  const sorted = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  return Object.fromEntries(sorted);
}

export default function TechStackCard({ tools, hasEnriched }: Props) {
  const grouped = groupByCategory(tools);

  return (
    <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6 flex flex-col">
      <h2 className="text-base font-semibold text-app-ink mb-4 flex items-center gap-2">
        <Wrench className="w-5 h-5 text-forest-600" /> Technology Stack
        {tools.length > 0 && (
          <span className="text-xs font-normal text-app-muted ml-1">({tools.length} detected)</span>
        )}
      </h2>
      {tools.length === 0 ? (
        <div className="text-center py-8 flex-1 flex flex-col items-center justify-center">
          <Clock className="w-8 h-8 text-app-line-strong mx-auto mb-2" />
          <p className="text-sm text-app-muted">
            {hasEnriched ? 'No tools detected' : 'Tool detection runs during enrichment'}
          </p>
        </div>
      ) : (
        <div className="space-y-4 max-h-[480px] overflow-y-auto pr-1 custom-scrollbar">
          {Object.entries(grouped).map(([category, categoryTools]) => (
            <div key={category}>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xs font-semibold text-app-muted uppercase tracking-wider">{category}</h3>
                <div className="flex-1 h-px bg-app-canvas" />
                <span className="text-xs text-app-muted">{categoryTools.length}</span>
              </div>
              <div className="space-y-1.5">
                {categoryTools.map(tool => {
                  const conf = confidenceConfig[tool.confidence] || confidenceConfig.medium;
                  const ConfIcon = conf.icon;
                  return (
                    <div
                      key={tool.id}
                      className="flex items-center gap-3 p-2.5 bg-app-canvas rounded-lg hover:bg-app-canvas transition-colors group"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-app-ink truncate">{tool.tool_name}</p>
                          {tool.website_url && (
                            <a
                              href={sanitizeUrl(tool.website_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <ExternalLink className="w-3 h-3 text-app-muted hover:text-forest-600" />
                            </a>
                          )}
                        </div>
                        {tool.description && (
                          <p className="text-xs text-app-muted truncate mt-0.5">{tool.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {tool.source && (
                          <span className="hidden sm:inline-flex text-[10px] text-app-muted max-w-[140px] truncate">{tool.source}</span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${conf.color}`}>
                          <ConfIcon className="w-2.5 h-2.5" />
                          {conf.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
